/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EmailInput from "../components/auth/EmailInput";
import UIButton from "../components/Button";
import "../styles/auth.css";

const baseUrl = process.env.REACT_APP_API_CONFIG;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");
    if (!isEmailValid) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${baseUrl}/auth/request-password-reset`, { email });
      setSubmitted(true);
    } catch {
      // Backend intentionally returns 200 even for unknown email, so a real
      // error here means a server/network problem.
      setError("Could not request password reset. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-6 py-8 max-w-md mx-auto">
      <h1 className="text-2xl font-semibold text-white">Reset password</h1>
      {submitted ? (
        <>
          <p className="text-color-desc">
            If an account exists for <strong>{email}</strong>, we've sent a
            password-reset link. It expires in 1 hour.
          </p>
          <UIButton
            text="Back to login"
            onClick={() => navigate("/get-started")}
            backgroundColor="btn-color"
            textColor="text-base"
          />
        </>
      ) : (
        <>
          <p className="text-color-desc">
            Enter the email associated with your account and we'll send you a
            reset link.
          </p>
          <EmailInput
            email={email}
            setEmail={setEmail}
            setIsValid={setIsEmailValid}
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <UIButton
            text={loading ? "Sending..." : "Send reset link"}
            onClick={handleSubmit}
            backgroundColor="btn-color"
            textColor="text-base"
            disabled={!isEmailValid || loading}
          />
          <button
            onClick={() => navigate("/get-started")}
            className="text-color-desc text-sm mt-2"
          >
            Back to login
          </button>
        </>
      )}
    </div>
  );
};

export default ForgotPassword;
